import React, { ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import cn from "classnames";

interface AnimatedCardProps {
    revealed: boolean;
    hide?: boolean;
    collapsed?: boolean;
    status?: "Active" | "Cancelled" | "In Progress" | "Success" | "Failed" | "Rejected" | "Migrated/Unknown";
    guid?: string;
    tokenAddress?: string;
    className?: string;
    children: ReactNode;
}

const AnimatedCard: React.FC<AnimatedCardProps> = ({
    revealed,
    hide = false,
    collapsed = false,
    status = "Active",
    guid,
    tokenAddress,
    className,
    children,
}) => {
    // Animation values for each layer
    const cardOpacity = hide ? 0 : status === "Cancelled" || status === "Failed" ? 0.5 : 1;
    const marginHide = -8;
    const marginInitial = hide ? marginHide : 0;
    const contentOpacity = hide ? 0 : 1;
    const contentHeight = hide ? 0 : collapsed ? 25 : "auto";
    const baseDelay = 0.1;
    // Delays for open (hide=false) and close (hide=true)
    const delays = hide
        ? [baseDelay * 2, baseDelay, 0] // closing: outer, middle, inner
        : [0, baseDelay * 2, baseDelay * 3]; // opening: outer, middle, inner
    const outerTransition = { duration: collapsed ? 0.2 : 0.3, ease: "easeInOut", delay: delays[0] };
    const middleTransition = { duration: collapsed ? 0.2 : 0.3, ease: "easeInOut", delay: delays[1] };
    const contentTransition = { duration: collapsed ? 0.2 : 0.3, ease: "easeInOut", delay: delays[2] };

    return (
        <AnimatePresence>
            {revealed && !hide && (
                // Outermost: handles fade/height for the group
                <motion.div
                    key={guid || tokenAddress}
                    initial={{ margin: marginHide, opacity: 0, height: "auto" }}
                    animate={{ margin: marginInitial, opacity: 1, height: "auto" }}
                    exit={{ margin: marginHide, opacity: 0, height: 0 }}
                    transition={outerTransition}
                    layout
                    className={cn("flex flex-col gap-4 justify-end min-w-full max-w-full relative", className)}
                >
                    {/* Middle: handles card opacity and marginTop (status/cancelled/fade) */}
                    <motion.div
                        className={cn(
                            "card w-full flex flex-col h-fit max-h-fit min-w-full overflow-hidden max-w-full",
                        )}
                        initial={{ height: "auto" }}
                        animate={{ opacity: cardOpacity }}
                        exit={{ opacity: 0 }}
                        transition={middleTransition}
                    >
                        {/* Innermost: handles content opacity and height (collapsed/hide) */}
                        <motion.div
                            initial={{ opacity: 0, height: contentHeight }}
                            animate={{ opacity: contentOpacity, height: contentHeight }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={contentTransition}
                            layout
                            className={cn("overflow-hidden gap-4 flex flex-col")}
                        >
                            {children}
                        </motion.div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default AnimatedCard;
